import { signout } from '@api/authentication';
import { CalendarCheck, CircleUser, ClipboardList, House, LogOut } from 'lucide-react';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import { isLoggedIn, profileState } from '@recoil/atoms';

interface Props {
    setOpenPaymentMethod?: Function;
    setOpenAppointmentForm?: Function;
    setOpenMyBookings?: Function;
    setOpenMyAccount?: Function;
    setOpenServiceRequest?: Function;
    setShowLoginModal?: (val: boolean) => void;
    openMyBookings?: boolean;
    openMyAccount?: boolean;
    openServiceRequest?: boolean;
    hideServiceTab?: boolean;
}

export const NavbarV3: React.FC<Props> = ({
    setOpenPaymentMethod,
    setOpenAppointmentForm,
    setOpenMyBookings,
    setOpenMyAccount,
    setOpenServiceRequest,
    setShowLoginModal,
    openMyBookings = false,
    openMyAccount = false,
    openServiceRequest = false,
    hideServiceTab = false,
}) => {
    const [profileInitial, setProfileInitial] = useState<string | null>(null);
    const [showLogoutSheet, setShowLogoutSheet] = useState(false);
    const router = useRouter();
    const [loggedIn, setLoggedIn] = useRecoilState(isLoggedIn);
    const [, setUser] = useRecoilState(profileState);
    const sheetRef = useRef<HTMLDivElement>(null);

    // Read profile initial for avatar
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const profile = JSON.parse(localStorage.getItem('profile') || 'null');
        if (profile?.full_name) {
            setProfileInitial(profile.full_name.charAt(0).toUpperCase());
        } else if (profile?.first_name) {
            setProfileInitial(profile.first_name.charAt(0).toUpperCase());
        } else {
            setProfileInitial(null);
        }
    }, [loggedIn]);

    // Close logout sheet on outside tap
    useEffect(() => {
        if (!showLogoutSheet) return;
        const handler = (e: MouseEvent) => {
            if (sheetRef.current && !sheetRef.current.contains(e.target as Node)) {
                setShowLogoutSheet(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showLogoutSheet]);

    const closeAllModals = () => {
        setOpenPaymentMethod?.(false);
        setOpenAppointmentForm?.(false);
        setOpenMyAccount?.(false);
        setOpenMyBookings?.(false);
        setOpenServiceRequest?.(false);
    };

    const handleLogout = () => {
        signout()
            .catch(() => {})
            .finally(() => {
                setLoggedIn(false);
                setUser(null);
                localStorage.removeItem('token');
                localStorage.removeItem('profile');
                document.cookie = 'access_token=;expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
                setShowLogoutSheet(false);
                closeAllModals();
                router.push(router.asPath);
            });
    };

    const handleAccountTap = () => {
        if (!loggedIn) {
            // Not logged in → open login modal
            closeAllModals();
            setShowLoginModal?.(true);
        } else if (openMyAccount) {
            // Already open → second tap shows logout sheet
            setShowLogoutSheet(prev => !prev);
        } else {
            // Logged in, panel closed → open account
            closeAllModals();
            setOpenMyAccount?.(true);
        }
    };

    const isHome = !openMyAccount && !openMyBookings && !openServiceRequest;
    const activeClass  = 'text-brand-500';
    const defaultClass = 'text-neutral-500';

    const NavItem = ({
        label, icon, active, onClick, badge,
    }: {
        label: string;
        icon: React.ReactNode;
        active?: boolean;
        onClick?: () => void;
        badge?: number;
    }) => (
        <li className="nav-item px-1 flex items-center select-none">
            <button
                className={`flex flex-col items-center gap-0.5 relative ${active ? activeClass : defaultClass}`}
                onClick={onClick}
            >
                {icon}
                <p className={`text-[12px] font-semibold leading-none font-manrope ${active ? 'text-brand-500' : 'text-neutral-500'}`}>
                    {label}
                </p>
                {badge > 0 && (
                    <span className="absolute -top-1 -right-2 flex justify-center items-center text-[10px] font-bold text-white bg-emerald-500 rounded-full h-4 w-4 leading-none">
                        {badge}
                    </span>
                )}
                {active && (
                    <span className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-brand-500" />
                )}
            </button>
        </li>
    );

    return (
        <>
            {/* ── Logout bottom sheet ─────────────────────────────────────── */}
            {showLogoutSheet && (
                <>
                    <div className="fixed inset-0 z-50 bg-black/30 md:hidden" />
                    <div
                        ref={sheetRef}
                        className="fixed bottom-[4.25rem] left-0 right-0 z-50 bg-white rounded-t-2xl shadow-xl border-t border-neutral-100 md:hidden"
                    >
                        {/* drag handle */}
                        <div className="flex justify-center pt-3 pb-1">
                            <div className="w-10 h-1 rounded-full bg-neutral-200" />
                        </div>

                        {/* profile row */}
                        {(() => {
                            const profile = typeof window !== 'undefined'
                                ? JSON.parse(localStorage.getItem('profile') || 'null')
                                : null;
                            return profile ? (
                                <div className="flex items-center gap-3 px-5 py-3 border-b border-neutral-100">
                                    <span
                                        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold font-manrope flex-shrink-0"
                                        style={{ backgroundColor: 'var(--brand-color, #ED7829)' }}
                                    >
                                        {profileInitial}
                                    </span>
                                    <div className="min-w-0">
                                        <p className="text-sm font-bold text-neutral-800 font-manrope truncate">
                                            {profile.full_name || profile.first_name || 'My Account'}
                                        </p>
                                        {profile.phone && (
                                            <p className="text-xs text-neutral-400 font-manrope">{profile.phone}</p>
                                        )}
                                    </div>
                                </div>
                            ) : null;
                        })()}

                        {/* actions */}
                        <div className="p-3 flex flex-col gap-1.5">
                            <button
                                onClick={handleLogout}
                                className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-red-500 hover:bg-red-50 transition-colors"
                            >
                                <LogOut size={18} />
                                <span className="text-sm font-semibold font-manrope">Log Out</span>
                            </button>
                            <button
                                onClick={() => setShowLogoutSheet(false)}
                                className="flex items-center justify-center w-full px-4 py-2.5 rounded-xl text-neutral-500 bg-neutral-50 text-sm font-semibold font-manrope"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* ── Bottom nav ──────────────────────────────────────────────── */}
            <nav className="z-40 bg-white border-t border-neutral-100 shadow-[0_-1px_8px_rgba(0,0,0,0.06)] fixed h-[4.25rem] flex justify-center bottom-0 left-0 right-0 md:hidden">
                <ul className="flex flex-row justify-between items-center w-full box-border py-1.5 px-5">

                    <NavItem
                        label="Home"
                        active={isHome}
                        icon={<House size={26} strokeWidth={isHome ? 2.2 : 1.6} />}
                        onClick={closeAllModals}
                    />

                    <NavItem
                        label="Bookings"
                        active={openMyBookings}
                        icon={<CalendarCheck size={26} strokeWidth={openMyBookings ? 2.2 : 1.6} />}
                        onClick={() => {
                            closeAllModals();
                            setOpenMyBookings?.(true);
                        }}
                    />

                    {!hideServiceTab && (
                        <NavItem
                            label="Service"
                            active={openServiceRequest}
                            icon={<ClipboardList size={26} strokeWidth={openServiceRequest ? 2.2 : 1.6} />}
                            onClick={() => {
                                closeAllModals();
                                setOpenServiceRequest?.(true);
                            }}
                        />
                    )}

                    <NavItem
                        label="Account"
                        active={openMyAccount}
                        icon={
                            loggedIn && profileInitial ? (
                                <span
                                    className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold leading-none font-manrope transition-all ${openMyAccount ? 'ring-2 ring-brand-500 ring-offset-1' : ''}`}
                                    style={{ backgroundColor: 'var(--brand-color, #ED7829)' }}
                                >
                                    {profileInitial}
                                </span>
                            ) : (
                                <CircleUser size={26} strokeWidth={openMyAccount ? 2.2 : 1.6} />
                            )
                        }
                        onClick={handleAccountTap}
                    />

                </ul>
            </nav>
        </>
    );
};